<script setup lang="ts">
    /**
     * Content som ska renderas i footern i ReceptforskrivningDisplay.vue
     */

    import ArrowLeftIcon from "@/assets/arrow_left_blue_thick.svg";
    import PlusIcon from "@/assets/plus_circle_white.svg";
    import SendIcon from "@/assets/send_white.svg";
    import SignFrame from "@/components/Auth/SignFrame.vue";
    import KButton from "@/components/Button.vue";
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { RenderDirection } from "@/components/forms/types";
    import KDialog from "@/components/generic/KDialog.vue";
    import { useStore } from "@/store";
    import { StoredReceptforskrivning } from "@/store/modules/recept/receptforskrivningModule";
    import { sendForskrivningar } from "@/tabs/Receptforskrivning/helpers";
    import ReceptforskrivningStart from "@/tabs/Receptforskrivning/Start/ReceptforskrivningStart.vue";
    import TabFooter from "@/tabs/TabFooter.vue";
    import { flashMessage, openDialog } from "@/utils";
    import { Portal } from "portal-vue";
    import emitter from "tiny-emitter/instance";
    import { computed, ref, toRefs } from "vue";

    const store = useStore();

    const props = defineProps<{
        /**
         * Används för att targetta rätt tab med portalen.
         */
        tabId: number;
        /**
         * Används för bland annat hämtning av receptförskrivning och ifyllnad av förhandsvisning
         */
        receptforskrivningId?: string;
        editingSamling?: boolean;
        // Är det en förnyelse av en förskrivning?
        fornya?: boolean;
    }>();

    const emit = defineEmits<{
        (e: "action", payload: { step: "form" | "display" | "list"; id?: string }): void;
        (e: "cancel"): void;
    }>();

    const { receptforskrivningId, tabId, editingSamling, fornya } = toRefs(props);

    const isLoading = ref(false);
    const isSigning = ref(false);
    const sendDialogRef = ref();

    const receptforskrivning = computed<StoredReceptforskrivning | null>(() =>
        store.getters["receptforskrivning/receptforskrivningById"]({
            id: receptforskrivningId?.value,
        })
    );
    const isSamling = computed(() => {
        const amount = (store.getters["receptforskrivning/receptforskrivningar"]?.length as number) ?? 0;

        return amount > 1;
    });
    const profil = computed(() => store.getters["userData/currentProfile"]);
    const patient = computed(() => store.getters["patientData/patient"]);

    const onAction = ({ step, id }: { step: "form" | "display" | "list"; id?: string }) => {
        // Step "list" innebär ett tillägg till en förskrivningssamling
        if (step === "list") {
            const receptforskrivningar: StoredReceptforskrivning[] =
                store.getters["receptforskrivning/receptforskrivningar"];

            receptforskrivningar.forEach((storedReceptforskrivning) => {
                if (receptforskrivning.value?.id && storedReceptforskrivning.id !== receptforskrivning.value.id) {
                    store.commit("receptforskrivning/partialUpdateReceptforskrivning", {
                        id: storedReceptforskrivning.id,
                        receptforskrivning: {
                            apotek: receptforskrivning?.value?.apotek,
                        },
                    });
                }
            });
        }

        emit("action", { step, id });
    };

    const onEdit = async () => {
        onAction({ step: "form", id: receptforskrivningId?.value });
    };

    const onSubmit = async () => {
        if (!receptforskrivning?.value) {
            return;
        }

        try {
            await sendForskrivningar({
                fornya: fornya?.value,
                receptforskrivningar: [receptforskrivning.value],
                headers: store.getters["nll/headers"],
            });

            flashMessage("Förskrivning skickad");
        } catch (error) {
            openDialog({
                type: "error",
                description: "Receptförskrivningen kunde inte skickas.",
                lines: (error as Error).message.split("\n"),
            });

            throw error;
        }
    };

    const toggleSigning = () => {
        isSigning.value = !isSigning.value;
    };

    const onSign = async () => {
        if (receptforskrivning?.value?.validationMessages?.length) {
            sendDialogRef?.value?.show();

            return;
        }

        toggleSigning();
    };

    const onSignError = () => {
        toggleSigning();

        openDialog("Misslyckades med att signera receptförskrivningen.", "error");
    };

    const onSend = async () => {
        isSigning.value = false;

        try {
            isLoading.value = true;
            await onSubmit();

            store.dispatch("tabs/closeTab", tabId.value);
            store.dispatch("tabs/openTab", {
                component: ReceptforskrivningStart,
                parameters: { redigera: false },
            });
        } catch (error) {
            console.error(error);
        } finally {
            isLoading.value = false;
        }
    };

    const onAdd = ({ step = "form" }: { step?: "form" | "display" | "list" }) => {
        if (receptforskrivning?.value?.validationMessages?.length) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: `Du försöker lägga till en förskrivning med varningar${
                    step === "list" ? " till samlingen" : ""
                }. Vill du lägga till den ändå?`,
                lines: receptforskrivning.value.validationMessages.map((message) => message.display),
                buttons: [
                    {
                        title: "Nej, återgå till förskrivning",
                        type: "secondary",
                    },
                    {
                        title: "Ja, lägg till ändå",
                        type: "primary",
                        action: async () => {
                            flashMessage(
                                step === "list"
                                    ? "Receptförskrivning tillagd i samlingen"
                                    : "Receptförskrivning tillagd i väntan på att skickas."
                            );
                            onAction({ step });
                        },
                    },
                ],
            });

            return;
        }

        flashMessage(
            step === "list"
                ? "Receptförskrivning tillagd i samlingen"
                : "Receptförskrivning tillagd i väntan på att skickas."
        );
        onAction({ step });
    };

    const onCancel = () => {
        if (store.getters["receptforskrivning/receptforskrivningar"].length > 1) {
            if (editingSamling?.value) {
                emitter.emit("open-dialog", {
                    type: "warning",
                    description: "Vill du avbryta redigeringen?",
                    buttons: [
                        {
                            title: "Fortsätt redigera förskrivningen",
                            type: "secondary",
                        },
                        {
                            title: "Ja, stäng utan ändringar",
                            type: "primary",
                            action: () => {
                                emit("action", { step: "list" });
                            },
                        },
                    ],
                });
            } else {
                emitter.emit("open-dialog", {
                    type: "warning",
                    description: "Förskrivningen har inte skickats. Vill du stänga utan att skicka den?",
                    buttons: [
                        {
                            title: "Fortsätt redigera förskrivningen",
                            type: "secondary",
                        },
                        {
                            title: "Ja, stäng utan att skicka den",
                            type: "primary",
                            action: () => {
                                store.commit("receptforskrivning/removeReceptforskrivning", {
                                    id: receptforskrivningId?.value,
                                });
                                onAction({ step: "list" });
                            },
                        },
                    ],
                });
            }

            return;
        }

        emitter.emit("open-dialog", {
            type: "warning",
            description: "Förskrivningen har inte skickats. Vill du stänga utan att skicka den?",
            buttons: [
                {
                    title: "Fortsätt redigera förskrivningen",
                    type: "secondary",
                },
                {
                    title: "Ja, stäng utan att skicka den",
                    type: "primary",
                    action: () => {
                        store.commit("receptforskrivning/removeReceptforskrivning", {
                            id: receptforskrivningId?.value,
                        });

                        emit("cancel");
                    },
                },
            ],
        });
    };

    const onList = () => {
        onAdd({ step: "list" });
    };
</script>

<template>
    <portal :to="`tab-${tabId}`">
        <TabFooter>
            <div class="receptforskrivning-display-portal">
                <KForm id="receptforskrivning-display-portal" actionsVariant="slim">
                    <template #default>
                        <div class="form-content">
                            <KFormFieldDisplay
                                name="apotek"
                                label="Skicka till apotek"
                                validation="required"
                                class="span-two"
                                :value="receptforskrivning?.apotek"
                                #default="{ value }"
                                :contentDirection="RenderDirection.Vertical"
                            >
                                <span class="apotek-label">{{ value.label }}</span>
                                <span class="apotek-adress">{{ value.adress }}</span>
                            </KFormFieldDisplay>
                            <KFormFieldDisplay
                                name="forhandsvisning"
                                label="Förhandsvisning doseringsanvisning"
                                :value="receptforskrivning?.forhandsvisning"
                                class="span-all"
                                :showEmptyValue="true"
                            />
                        </div>
                    </template>
                    <template #actions>
                        <KButton type="button" size="slim" variant="contour" @click="onEdit">
                            <img :src="ArrowLeftIcon" alt="Redigera förskrivning" />
                            <span>Redigera förskrivning</span>
                        </KButton>
                        <KButton
                            v-if="!isSamling"
                            type="button"
                            size="slim"
                            variant="standard"
                            :isLoading="isLoading"
                            @click="onSign"
                        >
                            <img :src="SendIcon" alt="Skicka förskrivning" />
                            <span>Skicka förskrivning</span>
                        </KButton>
                        <KButton
                            v-if="isSamling && !fornya"
                            type="button"
                            size="slim"
                            variant="standard"
                            @click="onList"
                        >
                            <span>Gå vidare till skicka samling</span>
                        </KButton>
                        <KButton v-if="!fornya" type="button" size="small" variant="standard" @click="onAdd">
                            <img :src="PlusIcon" alt="Lägg till förskrivning" />
                            <span>Lägg till förskrivning</span>
                        </KButton>
                        <KButton type="button" size="small" variant="contour" @click="onCancel">
                            <span>Avbryt</span>
                        </KButton>
                    </template>
                </KForm>
            </div>
        </TabFooter>
        <KDialog
            ref="sendDialogRef"
            variant="warning"
            acceptContent="Ja, skicka ändå"
            cancelContent="Nej, återgå till redigera förskrivning"
            :acceptAction="toggleSigning"
        >
            <template #contentTitle>
                <span>Du försöker skicka en förskrivning med varningar. Vill du skicka ändå?</span>
            </template>
            <template #default>
                <div v-for="display in receptforskrivning?.validationMessages.map((message) => message.display)">
                    {{ display }}
                </div>
            </template>
        </KDialog>
        <SignFrame
            v-if="isSigning"
            @success="onSend"
            @cancel="toggleSigning"
            @error="onSignError"
            title="Receptförskrivning"
            url="/nll/forskrivningar/"
            :data="{ patient: patient.id, accountableHealthcareProfessional: profil?.id }"
        />
    </portal>
</template>

<style scoped lang="scss">
    @import "@/style/form";

    .receptforskrivning-display-portal {
        .apotek-label,
        .apotek-adress {
            text-transform: capitalize;
        }

        .apotek-adress {
            font-size: 0.8rem;
        }
    }
</style>
